import {
  Box,
  Skeleton,
  Tooltip,
  Typography,
  styled,
  tooltipClasses,
} from "@mui/material"
import { useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router-dom"
import { DELIVERY_OPTIONS } from "src/app/constants"
import { currencyFormat } from "src/app/utils"
import {
  aboveMinimumRateSelector,
  amountForFreeShippingSelector,
  hasFreeShippingDiscountCodeSelector,
  lineItemsDiscountedSubTotalPriceSelector,
  minimumRateSelector,
  setSelectedShopPolicy,
} from "src/features/checkout/checkoutSlice"

const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    color: "white",
    fontSize: 13,
    fontWeight: 400,
    padding: 12,
    maxWidth: 140,
    textAlign: "center",
  },
})

export default function CartSummary() {
  const dispatch = useDispatch()
  const location = useLocation()
  const cart = useSelector((state) => state.checkout.cart)
  const aboveMinimumRate = useSelector(aboveMinimumRateSelector)
  const minimumRate = useSelector(minimumRateSelector)
  const hasFreeShippingDiscountCode = useSelector(
    hasFreeShippingDiscountCodeSelector,
  )
  const amountForFreeShipping = useSelector(amountForFreeShippingSelector)
  const lineItemsDiscountedSubTotalPrice = useSelector(
    lineItemsDiscountedSubTotalPriceSelector,
  )
  const { deliveryOption } = useSelector((state) => state.checkout.options)

  return (
    <>
      <Box sx={{ ...styles.itemContainer, mt: 3 }}>
        <span>Subtotal</span>
        {cart.cost.subtotalAmount ? (
          <span style={styles.amount}>
            {currencyFormat(lineItemsDiscountedSubTotalPrice)}
          </span>
        ) : (
          <Skeleton width={80} />
        )}
      </Box>

      {cart.discountAllocations?.edges.filter(
        ({ node }) =>
          node.applicable &&
          node.targetSelection === "ALL" &&
          node.targetType === "LINE_ITEM",
      ).length > 0 && (
        <>
          <Box sx={styles.itemContainer}>
            <span>Order discount</span>
          </Box>
          {cart.discountAllocations?.edges.map((i, index) => {
            const { applicable, targetSelection, code, targetType, value } =
              i.node
            let discountAmount = parseFloat(value.amount)
            if (value.percentage) {
              discountAmount =
                lineItemsDiscountedSubTotalPrice * (value.percentage / 100)
            }

            if (
              applicable &&
              targetSelection === "ALL" &&
              targetType === "LINE_ITEM"
            ) {
              return (
                <Box
                  key={index}
                  sx={{ ...styles.itemContainer, ...{ color: "grey" } }}
                >
                  <span>
                    {discountCodeIcon}
                    {code?.toUpperCase()}
                  </span>
                  {discountAmount > 0 && (
                    <span>{`- ${currencyFormat(discountAmount)}`}</span>
                  )}
                </Box>
              )
            }
          })}
        </>
      )}

      <Box sx={styles.itemContainer}>
        <Box display="flex">
          {!deliveryOption
            ? "Delivery/Shipping"
            : [
                DELIVERY_OPTIONS.US_MAINLAND_SHIPPING,
                DELIVERY_OPTIONS.NEIGHBOR_ISLAND_SHIPPING,
              ].includes(deliveryOption)
            ? "Shipping"
            : "Delivery"}
          <span
            style={styles.moreInfoIcon}
            onClick={() => {
              dispatch(setSelectedShopPolicy("shippingPolicy"))
            }}
          >
            {moreInfoIcon}
          </span>
        </Box>
        {cart.cost.totalAmount ? (
          <span style={styles.amount}>
            {["/shipping", "/delivery"].includes(location.pathname) &&
            !aboveMinimumRate ? (
              <Typography fontSize="smaller" color="error">
                Order minimum to your location is {currencyFormat(minimumRate)}
              </Typography>
            ) : cart.deliveryGroups?.edges[0]?.node.selectedDeliveryOption?.estimatedCost.amount ? (
              hasFreeShippingDiscountCode ||
              parseFloat(cart.deliveryGroups?.edges[0]?.node.selectedDeliveryOption?.estimatedCost.amount) === 0 ? (
                "Free"
              ) : (
                <>
                  {amountForFreeShipping > 0 && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="primary"
                      fontWeight={500}
                      marginRight={1}
                    >
                      Just {currencyFormat(amountForFreeShipping)} more for free
                      delivery!
                    </Typography>
                  )}
                  {currencyFormat(cart.deliveryGroups?.edges[0]?.node.selectedDeliveryOption?.estimatedCost.amount)}
                </>
              )
            ) : (
              "To be calculated at next step"
            )}
          </span>
        ) : (
          <Skeleton width={80} />
        )}
      </Box>
      {cart.discountAllocations?.filter(
          ({ discountApplication }) =>
            discountApplication.targetSelection === "ALL" &&
            discountApplication.targetType === "SHIPPING_LINE",
        )
        .map(({ code, value }, index) => {
          let discountAmount = parseFloat(value.amount)
          if (value.percentage) {
            discountAmount =
              parseFloat(cart.deliveryGroups?.edges[0]?.node.selectedDeliveryOption?.price.amount) *
              (value.percentage / 100)
          }
          return (
            <Box
              key={index}
              sx={{ ...styles.itemContainer, ...{ color: "grey" } }}
            >
              <span>
                {discountCodeIcon}
                {code?.toUpperCase()}
              </span>
              {discountAmount > 0 && (
                <span>{`- ${currencyFormat(discountAmount)}`}</span>
              )}
            </Box>
          )
        })}

      {deliveryOption !== DELIVERY_OPTIONS.US_MAINLAND_SHIPPING && cart.cost && (
        <Box sx={styles.itemContainer}>
          <Box display="flex">
            Estimated taxes
            <CustomTooltip
              title="The final tax and total will be confirmed by email or text after you place your order."
              placement="top"
              arrow
            >
              <span style={styles.moreInfoIcon}>{moreInfoIcon}</span>
            </CustomTooltip>
          </Box>
          <span style={styles.amount}>
            {["/options"].includes(location.pathname)
              ? "To be calculated at next step"
              : parseFloat(cart.cost.totalTaxAmount?.amount) > 0
              ? currencyFormat(cart.cost.totalTaxAmount?.amount)
              : "To be calculated at next step"}
          </span>
        </Box>
      )}

      {cart.appliedGiftCards?.map((item, index) => (
        <Box key={index} sx={styles.itemContainer}>
          <Box display="flex" alignItems="center">
            Gift Card
            <img
              src={giftCardIcon}
              height={18}
              style={{ marginLeft: 4, marginRight: 4 }}
            />
            {" •••• "}
            {item.lastCharacters.toUpperCase()}
          </Box>
          {item.amountUsed ? (
            <span style={styles.amount}>
              - {currencyFormat(item.amountUsed.amount)}
            </span>
          ) : (
            <Skeleton width={80} />
          )}
        </Box>
      ))}

      <Box sx={{ ...styles.itemContainer, ...styles.paymentDue }}>
        <span>Total</span>
        {cart.cost.totalAmount ? (
          <span>
            {currencyFormat(cart.cost.totalAmount.amount)}
          </span>
        ) : (
          <Skeleton width={80} />
        )}
      </Box>
    </>
  )
}

const styles = {
  itemContainer: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "14px",
    mt: 2,
  },
  amount: {
    fontWeight: 500,
  },
  paymentDue: {
    fontSize: "17px",
    fontWeight: 500,
  },
  moreInfoIcon: {
    cursor: "pointer",
    height: 15,
    width: 15,
    marginLeft: 4,
    fill: "none",
    stroke: "rgb(102, 102, 102)",
  },
}

const discountCodeIcon = (
  <img
    src="data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPHN2ZyB2aWV3Qm94PSIwIDAgMTQgMTQiIHdpZHRoPSIxNCIgaGVpZ2h0PSIxNCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cGF0aCBzdHJva2UtbGluZWNhcD0icm91bmQiIGQ9Ik0gNi44NTggMS4zNCBMIDExLjgyMiAxLjM0IEMgMTIuMDE1IDEuMzQgMTIuMTcyIDEuNDk2IDEyLjE3MiAxLjY5IEwgMTIuMTcyIDYuNjU0IEMgMTIuMTcyIDYuODM5IDEyLjA5OCA3LjAxNyAxMS45NjcgNy4xNDkgTCA3LjA2NCAxMi4wNTMgQyA2Ljc5IDEyLjMyNiA2LjM0NyAxMi4zMjYgNi4wNzQgMTIuMDUzIEwgMS40NTkgNy40MzggQyAxLjE4NSA3LjE2NCAxLjE4NSA2LjcyMSAxLjQ1OSA2LjQ0OCBMIDYuMzY0IDEuNTQ1IEMgNi40OTUgMS40MTMgNi42NzMgMS4zNCA2Ljg1OSAxLjM0IEwgNi44NTggMS4zNCBaIiBzdHlsZT0iZmlsbDogbm9uZTsgc3Ryb2tlOiByZ2IoMTAyLCAxMDIsIDEwMik7Ii8+CiAgPGNpcmNsZSBjeD0iOS4xIiBjeT0iNC45IiByPSIwLjciIHN0eWxlPSJzdHJva2U6IHJnYigxMDIsIDEwMiwgMTAyKTsgZmlsbDogcmdiKDEwMiwgMTAyLCAxMDIpOyIvPgogIDxwYXRoIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIgZD0iTTkuMTAyIDQuODk3aC0uMDA1di4wMDVoLjAwNXYtLjAwNVoiLz4KPC9zdmc+"
    style={{ marginRight: 4 }}
  />
)

const giftCardIcon =
  "data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPHN2ZyB2aWV3Qm94PSIwIDAgMTQgMTQiIHdpZHRoPSIxNCIgaGVpZ2h0PSIxNCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cGF0aCBzdHJva2UtbGluZWNhcD0icm91bmQiIGQ9Ik0yLjEgN3Y1LjEzYzAgLjI2LjIxLjQ3LjQ3LjQ3SDdNMi4xIDdoLS4yM2EuNDcuNDcgMCAwIDEtLjQ3LS40N1Y0LjY3YzAtLjI2LjIxLS40Ny40Ny0uNDdoMS40ODdNMi4xIDdIN200LjkgMHY1LjEzYzAgLjI2LS4yMS40Ny0uNDcuNDdIN00xMS45IDdoLjIzYy4yNiAwIC40Ny0uMjEuNDctLjQ3VjQuNjdhLjQ3LjQ3IDAgMCAwLS40Ny0uNDdoLTEuNTg3TTExLjkgN0g3bTAgMHY1LjZtMC04LjRIMy4zNTdNNyA0LjJzLS4yNzYtMi44LTIuNTQtMi44Yy0yLjI2MyAwLTEuMTAzIDIuOC0xLjEwMyAyLjhNNyA0LjJoMy41NDNNNyA0LjJzLjE3Ni0yLjggMi40NC0yLjhjMi4yNjMgMCAxLjEwMyAyLjggMS4xMDMgMi44IiBzdHlsZT0iZmlsbDogbm9uZTsgc3Ryb2tlOiByZ2IoMTAyLCAxMDIsIDEwMik7Ii8+Cjwvc3ZnPg=="

const moreInfoIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 14 14"
    focusable="false"
    aria-label="More information"
  >
    <circle cx="7" cy="7" r="5.6"></circle>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M5.6 5.1c.2-1.3 2.6-1.3 2.8 0 .2 1.3-1.45 1.3-1.45 2.35m.055 2.35H7v.005h.005V9.8Z"
    ></path>
    <circle cx="7" cy="9.7" r="0.1"></circle>
  </svg>
)
